import { useCallback, useRef } from 'react';
import { applyNodeChanges, applyEdgeChanges } from '@xyflow/react';

// Reducers
import { myStore } from '~/App';
import { setEdges } from '~/reducers';
import { setNodes } from '~/reducers';
import { useSelector } from 'react-redux';

// Robust unique ID generator that avoids collisions with existing nodes
let id = 0;
let idCounterInitialized = false;
const ensureIdCounter = () => {
	if (idCounterInitialized) return;
	try {
		const state = myStore.getState();
		const nodes = state?.nodes?.nodes || [];
		let maxFound = -1;
		for (const n of nodes) {
			if (typeof n?.id === 'string') {
				const m = /^dndnode_(\d+)$/.exec(n.id);
				if (m) {
					const num = parseInt(m[1], 10);
					if (!Number.isNaN(num)) maxFound = Math.max(maxFound, num);
				}
			}
		}
		id = Math.max(id, maxFound + 1);
		idCounterInitialized = true;
	} catch {
		// fallback – keep default id
		idCounterInitialized = true;
	}
};

export const getId = () => {
	ensureIdCounter();
	const state = myStore.getState();
	const existingIds = new Set((state?.nodes?.nodes || []).map(n => n.id));
	let candidate;
	do {
		candidate = `dndnode_${id++}`;
	} while (existingIds.has(candidate));
	return candidate;
};

// Cores para edges de acordo com os handles da Decisão
const getEdgeColorsForHandle = (sourceHandle) => {
	if (sourceHandle === 'verdadeiro') {
		return { stroke: '#44ff44', marker: '#44ff44' };
	}
	if (sourceHandle === 'falso') {
		return { stroke: '#ff4444', marker: '#ff4444' };
	}
	return null;
};

// Aplica estilos (stroke/markerEnd.color) baseados no handle e mescla com defaultEdgeOptions
const withEdgeStyleForHandle = (edge, defaultEdgeOptions) => {
	const colorCfg = getEdgeColorsForHandle(edge.sourceHandle);
	const merged = { ...defaultEdgeOptions, ...edge };
	if (colorCfg) {
		merged.style = { ...(defaultEdgeOptions?.style || {}), ...(edge.style || {}), stroke: colorCfg.stroke };
		merged.markerEnd = { ...(defaultEdgeOptions?.markerEnd || {}), ...(edge.markerEnd || {}), color: colorCfg.marker };
	} else {
		// Ainda garante mescla básica
		if (defaultEdgeOptions?.style || edge.style) {
			merged.style = { ...(defaultEdgeOptions?.style || {}), ...(edge.style || {}) };
		}
		if (defaultEdgeOptions?.markerEnd || edge.markerEnd) {
			merged.markerEnd = { ...(defaultEdgeOptions?.markerEnd || {}), ...(edge.markerEnd || {}) };
		}
	}
	return merged;
};

// Gera um ID único para a edge com base nos endpoints e handles
const generateUniqueEdgeId = (edge, existingEdges = []) => {
	const sHandlePart = edge.sourceHandle ? `${edge.sourceHandle}` : '';
	const tHandlePart = edge.targetHandle ? `${edge.targetHandle}` : '';
	const baseId = `xy-edge__${edge.source}${sHandlePart}-${edge.target}${tHandlePart}`;
	let uniqueId = baseId;
	if (existingEdges.some(e => e.id === uniqueId)) {
		const count = existingEdges.filter(e => e.id.startsWith(baseId)).length;
		uniqueId = `${baseId}__${count + 1}`;
	}
	return uniqueId;
};

// Função para organizar nós centralizados em linha com espaçamento adequado
export const organizeNodesLayout = (options = {}) => {
	const {
		spacing = 300,        // Espaçamento base entre nós (aumentado de 200 para 300)
		arrowSpacing = 30,    // Espaçamento extra para setas
		startX = 300,         // Posição X inicial (centralizada)
		startY = 50,          // Posição Y inicial
		centerHorizontally = true, // Centralizar horizontalmente
		mode = 'vertical'     // 'vertical', 'horizontal', 'auto'
	} = options;
	
	const nodes = myStore.getState().nodes.nodes;
	const edges = myStore.getState().edges.edges;
	
	if (nodes.length === 0) return;
	
	// Cria um mapa de nós por ID para facilitar a busca
	const nodesMap = nodes.reduce((map, node) => {
		map[node.id] = node;
		return map;
	}, {});
	
	// Encontra o nó inicial (geralmente com ID "1" ou tipo "Inicio")
	const startNode = nodes.find(node => node.id === "1" || node.type === "Inicio") || nodes[0];
	
	// Organiza os nós em sequência seguindo as conexões
	const orderedNodes = [];
	const visitedNodes = new Set();
	
	const traverseNodes = (currentNodeId) => {
		if (visitedNodes.has(currentNodeId)) return;
		
		const currentNode = nodesMap[currentNodeId];
		if (!currentNode) return;
		
		visitedNodes.add(currentNodeId);
		orderedNodes.push(currentNode);
		
		// Encontra as arestas que saem deste nó
		const outgoingEdges = edges.filter(edge => edge.source === currentNodeId);
		
		// Para nós de decisão, prioriza a saída "verdadeiro" depois "falso"
		if (currentNode.type === "Decidir") {
			const trueEdge = outgoingEdges.find(edge => edge.sourceHandle === "verdadeiro");
			const falseEdge = outgoingEdges.find(edge => edge.sourceHandle === "falso");
			
			if (trueEdge) traverseNodes(trueEdge.target);
			if (falseEdge) traverseNodes(falseEdge.target);
		} else {
			// Para outros nós, segue a primeira aresta disponível
			outgoingEdges.forEach(edge => traverseNodes(edge.target));
		}
	};
	
	traverseNodes(startNode.id);
	
	// Calcula posições baseadas no modo selecionado
	let currentX = startX;
	let currentY = startY;
	
	const updatedNodes = nodes.map(node => {
		const orderIndex = orderedNodes.findIndex(orderedNode => orderedNode.id === node.id);
		
		if (orderIndex !== -1) {
			// Obtém dimensões do nó (usa measured se disponível, senão valores padrão)
			const nodeWidth = node.measured?.width || 150;
			const nodeHeight = node.measured?.height || 50;
			
			// Calcula espaçamento baseado no nó anterior
			if (orderIndex > 0) {
				const previousNode = orderedNodes[orderIndex - 1];
				const previousWidth = previousNode.measured?.width || 150;
				const previousHeight = previousNode.measured?.height || 50;
				
				if (mode === 'vertical') {
					// Layout vertical: nós empilhados
					currentY += previousHeight + spacing + arrowSpacing;
				} else if (mode === 'horizontal') {
					// Layout horizontal: nós em linha
					currentX += previousWidth + spacing + arrowSpacing;
				}
			}
			
			// Calcula posição final
			let finalX = currentX;
			let finalY = currentY;
			
			// Centraliza horizontalmente se solicitado
			if (centerHorizontally && mode === 'vertical') {
				finalX = startX - (nodeWidth / 2);
			} else if (centerHorizontally && mode === 'horizontal') {
				finalY = startY - (nodeHeight / 2);
			}
			
			return {
				...node,
				position: {
					x: finalX,
					y: finalY
				}
			};
		}
		
		return node; // Mantém nós não conectados na posição original
	});
	
	// Atualiza os nós no store
	myStore.dispatch(setNodes(updatedNodes));
	
	console.log(`Layout organizado: ${orderedNodes.length} nós organizados em modo ${mode}`);
};

// Função específica para layout vertical centralizado
export const organizeNodesVertical = (reactFlowInstance = null) => {
	organizeNodesLayout({
		mode: 'vertical',
		spacing: 150,
		arrowSpacing: 40,
		startX: 300,
		startY: 50,
		centerHorizontally: true
	});
	
	// Aplica fitView se a instância do ReactFlow for fornecida
	if (reactFlowInstance && reactFlowInstance.fitView) {
		setTimeout(() => {
			reactFlowInstance.fitView({ padding: 0.1, duration: 500 });
		}, 100);
	}
};

// Função específica para layout horizontal
export const organizeNodesHorizontal = (reactFlowInstance = null) => {
	organizeNodesLayout({
		mode: 'horizontal',
		spacing: 200,
		arrowSpacing: 50,
		startX: 50,
		startY: 200,
		centerHorizontally: true
	});
	
	// Aplica fitView se a instância do ReactFlow for fornecida
	if (reactFlowInstance && reactFlowInstance.fitView) {
		setTimeout(() => {
			reactFlowInstance.fitView({ padding: 0.1, duration: 500 });
		}, 100);
	}
};

// Função avançada para layout considerando ramificações de decisão
export const organizeNodesWithBranches = (reactFlowInstance = null) => {
	const nodes = myStore.getState().nodes.nodes;
	const edges = myStore.getState().edges.edges;
	
	if (nodes.length === 0) return;
	
	const nodesMap = nodes.reduce((map, node) => {
		map[node.id] = node;
		return map;
	}, {});
	
	// Encontra o nó inicial
	const startNode = nodes.find(node => node.id === "1" || node.type === "Inicio") || nodes[0];
	
	// Estrutura para armazenar posições calculadas
	const nodePositions = new Map();
	const processedNodes = new Set();
	
	// Configurações do layout
	const config = {
		verticalSpacing: 180,
		horizontalSpacing: 250,
		branchOffset: 200,
		startX: 300,
		startY: 50
	};
	
	// Função recursiva para calcular posições
	const calculatePositions = (nodeId, x, y) => {
		if (processedNodes.has(nodeId)) return y;
		
		const node = nodesMap[nodeId];
		if (!node) return y;
		
		processedNodes.add(nodeId);
		
		// Obtém dimensões do nó
		const nodeWidth = node.measured?.width || 150;
		const nodeHeight = node.measured?.height || 50;
		
		// Calcula posição centralizada
		const centeredX = x - (nodeWidth / 2);
		nodePositions.set(nodeId, { x: centeredX, y });
		
		// Encontra arestas de saída
		const outgoingEdges = edges.filter(edge => edge.source === nodeId);
		
		if (node.type === "Decidir" && outgoingEdges.length > 1) {
			// Nó de decisão: organiza ramificações
			const trueEdge = outgoingEdges.find(edge => edge.sourceHandle === "verdadeiro");
			const falseEdge = outgoingEdges.find(edge => edge.sourceHandle === "falso");
			
			let maxY = y + nodeHeight + config.verticalSpacing;
			
			if (trueEdge) {
				const branchX = x + config.branchOffset;
				maxY = Math.max(maxY, calculatePositions(trueEdge.target, branchX, y + nodeHeight + config.verticalSpacing, 'right'));
			}
			
			if (falseEdge) {
				const branchX = x - config.branchOffset;
				maxY = Math.max(maxY, calculatePositions(falseEdge.target, branchX, y + nodeHeight + config.verticalSpacing, 'left'));
			}
			
			return maxY;
		} else {
			// Nó normal: continua linearmente
			let maxY = y;
			outgoingEdges.forEach(edge => {
				maxY = Math.max(maxY, calculatePositions(edge.target, x, y + nodeHeight + config.verticalSpacing));
			});
			return maxY;
		}
	};
	
	// Calcula todas as posições
	calculatePositions(startNode.id, config.startX, config.startY);
	
	// Aplica as posições calculadas
	const updatedNodes = nodes.map(node => {
		const position = nodePositions.get(node.id);
		return position ? { ...node, position } : node;
	});
	
	// Atualiza os nós no store
	myStore.dispatch(setNodes(updatedNodes));
	
	console.log(`Layout com ramificações organizado: ${nodePositions.size} nós posicionados`);
	
	// Aplica fitView se a instância do ReactFlow for fornecida
	if (reactFlowInstance && reactFlowInstance.fitView) {
		setTimeout(() => {
			reactFlowInstance.fitView({ padding: 0.1, duration: 500 });
		}, 100);
	}
};

// Função utilitária para auto-organizar nós quando necessário
export const autoOrganizeIfNeeded = (reactFlowInstance = null) => {
	const nodes = myStore.getState().nodes.nodes;
	
	// Verifica se há nós sobrepostos ou muito próximos
	let needsOrganization = false;
	
	for (let i = 0; i < nodes.length; i++) {
		for (let j = i + 1; j < nodes.length; j++) {
			const node1 = nodes[i];
			const node2 = nodes[j];
			
			const distance = Math.sqrt(
				Math.pow(node1.position.x - node2.position.x, 2) + 
				Math.pow(node1.position.y - node2.position.y, 2)
			);
			
			// Se a distância for muito pequena, precisamos organizar
			if (distance < 100) {
				needsOrganization = true;
				break;
			}
		}
		if (needsOrganization) break;
	}
	
	if (needsOrganization) {
		console.log('Nós sobrepostos detectados, organizando automaticamente...');
		organizeNodesVertical(reactFlowInstance);
	}
};

// Função para ajustar espaçamento baseado no conteúdo dos nós
export const getOptimalSpacing = (node1, node2) => {
	const height1 = node1.measured?.height || 50;
	const height2 = node2.measured?.height || 50;
	
	// Espaçamento base de 50px, mais um adicional baseado na altura dos nós
	const baseSpacing = 50;
	const heightFactor = Math.max(height1, height2) * 0.3;
	
	return Math.ceil(baseSpacing + heightFactor);
};

// Função personalizada para lidar com eventos do fluxo
export const useFlowFunctions = (
	screenToFlowPosition,
	defaultEdgeOptions,
	highlightedEdgeIds,
	setHighlightedEdgeIds
) => {
	const edges = useSelector((state) => state.edges.edges);
	const nodes = useSelector((state) => state.nodes.nodes);

	// Controla estado de arrasto e remoções adiadas para evitar erros internos do XYFlow
	const isDraggingRef = useRef(false);
	const pendingRemovalRef = useRef(new Set());

	// Remove um nó com reconexão das arestas conforme regras atuais
	const performNodeRemoval = useCallback((nodeId) => {
		const actuallyEdges = myStore.getState().edges.edges;
		const actuallyNodes = myStore.getState().nodes.nodes;

		// Limpa tooltip/realces transitórios
		const edgesClean = actuallyEdges.map(e => {
			if (e?.style?.strokeDasharray) {
				const ns = { ...e.style };
				delete ns.strokeDasharray;
				return { ...e, style: ns };
			}
			return e;
		});
		myStore.dispatch(setEdges(edgesClean));
		setHighlightedEdgeIds([]);
		window.dispatchEvent(new CustomEvent('connectPreviewTooltip', { detail: { show: false } }));

		const connectedEdges = actuallyEdges.filter(
			(e) => e.source === nodeId || e.target === nodeId
		);
		const incomingEdges = connectedEdges.filter((e) => e.target === nodeId);
		const outgoingEdges = connectedEdges.filter((e) => e.source === nodeId);

		let newEdges = [];
		if (incomingEdges.length === 1 && outgoingEdges.length >= 1) {
			const incomingEdge = incomingEdges[0];
			const outgoingEdge = outgoingEdges[0];

			let newEdge = {
				id: `xy-edge__${incomingEdge.source}-${outgoingEdge.target}`,
				source: incomingEdge.source,
				target: outgoingEdge.target,
				...defaultEdgeOptions,
			};

			// preserva handle e estilo se vier de decisão
			if (incomingEdge.sourceHandle === 'verdadeiro' || incomingEdge.sourceHandle === 'falso') {
				newEdge.sourceHandle = incomingEdge.sourceHandle;
			}
			newEdge.id = generateUniqueEdgeId(newEdge, actuallyEdges);
			newEdge = withEdgeStyleForHandle(newEdge, defaultEdgeOptions);

			newEdges = actuallyEdges
				.filter(
					(e) =>
						e.id !== incomingEdge.id &&
						e.id !== outgoingEdge.id &&
						e.source !== nodeId &&
						e.target !== nodeId
				)
				.concat(newEdge);
		} else {
			newEdges = actuallyEdges.filter((e) => e.source !== nodeId && e.target !== nodeId);
		}

		// Aplica arestas atualizadas
		myStore.dispatch(setEdges(newEdges));
		// Remove o nó do store
		const prunedNodes = actuallyNodes.filter(n => n.id !== nodeId);
		myStore.dispatch(setNodes(prunedNodes));
	}, [defaultEdgeOptions, setHighlightedEdgeIds]);

	// Ao soltar um node já existente sobre uma edge: insere o node na edge mais próxima
	const onNodeDragStop = useCallback((event, draggedNode) => {
		if (!draggedNode) return;
		// marca fim do arrasto para permitir remoções pendentes
		isDraggingRef.current = false;
		// Se o nó foi deletado durante o arrasto, apenas limpa UI e sai
		const currentNodes = myStore.getState().nodes.nodes || [];
		const stillExists = currentNodes.some(n => n.id === draggedNode.id);
		if (!stillExists) {
			// limpa highlight e tooltip
			const edgesAtual = myStore.getState().edges.edges;
			const cleaned = edgesAtual.map(e => {
				if (e?.style?.strokeDasharray) {
					const ns = { ...e.style };
					delete ns.strokeDasharray;
					return { ...e, style: ns };
				}
				return e;
			});
			myStore.dispatch(setEdges(cleaned));
			setHighlightedEdgeIds([]);
			window.dispatchEvent(new CustomEvent('connectPreviewTooltip', { detail: { show: false } }));
			// processa remoções que foram adiadas
			if (pendingRemovalRef.current.size) {
				const ids = Array.from(pendingRemovalRef.current);
				pendingRemovalRef.current.clear();
				ids.forEach(id => performNodeRemoval(id));
			}
			return;
		}
		const edgesAtualizados = myStore.getState().edges.edges;
		const allNodes = myStore.getState().nodes.nodes;

		const dropPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });

		const getNodeDims = (n) => ({
			w: n?.measured?.width || 150,
			h: n?.measured?.height || 50,
		});

		const hasEdge = (src, trg, handle) => {
			return edgesAtualizados.some(e => e.source === src && e.target === trg && (handle ? e.sourceHandle === handle : !e.sourceHandle));
		};

		const findClosestEdge = () => {
			let best = { edge: null, distance: Infinity };
			for (const edge of edgesAtualizados) {
				// Não tentar inserir em edge que já tem o node como source ou target
				if (edge.source === draggedNode.id || edge.target === draggedNode.id) continue;

				const sourceNode = allNodes.find((n) => n.id === edge.source);
				const targetNode = allNodes.find((n) => n.id === edge.target);
				if (!sourceNode || !targetNode) continue;
				// Ignora loop do Repetir
				if (sourceNode.type === 'Repetir' && edge.sourceHandle === 'loop') continue;

				const { w: sw, h: sh } = getNodeDims(sourceNode);
				const { w: tw } = getNodeDims(targetNode);

				let a, b;
				if (sourceNode.type === 'Decidir') {
					if (edge.sourceHandle === 'verdadeiro') {
						a = { x: sourceNode.position.x + sw, y: sourceNode.position.y + sh / 2 };
					} else if (edge.sourceHandle === 'falso') {
						a = { x: sourceNode.position.x, y: sourceNode.position.y + sh / 2 };
					} else {
						continue;
					}
					b = { x: targetNode.position.x + tw / 2, y: targetNode.position.y };
				} else {
					a = { x: sourceNode.position.x + sw / 2, y: sourceNode.position.y + sh };
					b = { x: targetNode.position.x + tw / 2, y: targetNode.position.y };
				}

				const d = distanceFromPointToLineSegment(dropPos, a, b);
				if (d < best.distance) {
					best = { edge, distance: d };
				}
			}
			return best;
		};

		const { edge: closestEdge, distance: closestDistance } = findClosestEdge();
		if (!closestEdge || closestDistance >= 20) {
			// limpa destaque caso tenha
			if (highlightedEdgeIds?.length) setHighlightedEdgeIds([]);
			return;
		}

		const sourceNodeHandle = closestEdge.sourceHandle;

		const buildReplacements = () => {
			// Evita edge de origem igual destino
			if (closestEdge.source === draggedNode.id || closestEdge.target === draggedNode.id) return null;

			if (draggedNode.type === 'Decidir') {
				let newEdge1 = {
					id: `xy-edge__${closestEdge.source}${sourceNodeHandle || ''}-${draggedNode.id}`,
					source: closestEdge.source,
					target: draggedNode.id,
					...defaultEdgeOptions,
				};
				if (sourceNodeHandle) newEdge1.sourceHandle = sourceNodeHandle;

				let newEdges = [];
				// adiciona primeira conexão se não existir
				if (!hasEdge(newEdge1.source, newEdge1.target, newEdge1.sourceHandle)) {
					newEdge1.id = generateUniqueEdgeId(newEdge1, edgesAtualizados);
					newEdge1 = withEdgeStyleForHandle(newEdge1, defaultEdgeOptions);
					newEdges.push(newEdge1);
				}

				// Saídas verdadeiro/falso do node Decidir para o target
				const outV = {
					id: `xy-edge__${draggedNode.id}verdadeiro-${closestEdge.target}`,
					source: draggedNode.id,
					sourceHandle: 'verdadeiro',
					target: closestEdge.target,
					...defaultEdgeOptions,
				};
				const outF = {
					id: `xy-edge__${draggedNode.id}falso-${closestEdge.target}`,
					source: draggedNode.id,
					sourceHandle: 'falso',
					target: closestEdge.target,
					...defaultEdgeOptions,
				};

				if (!edgesAtualizados.some(e => e.source === outV.source && e.sourceHandle === 'verdadeiro')) {
					outV.id = generateUniqueEdgeId(outV, edgesAtualizados);
					newEdges.push(withEdgeStyleForHandle(outV, defaultEdgeOptions));
				}
				if (!edgesAtualizados.some(e => e.source === outF.source && e.sourceHandle === 'falso')) {
					outF.id = generateUniqueEdgeId(outF, edgesAtualizados);
					newEdges.push(withEdgeStyleForHandle(outF, defaultEdgeOptions));
				}
				return newEdges;
			} else {
				// Nó comum
				let inEdge = {
					id: `xy-edge__${closestEdge.source}${sourceNodeHandle || ''}-${draggedNode.id}`,
					source: closestEdge.source,
					target: draggedNode.id,
					...defaultEdgeOptions,
				};
				if (sourceNodeHandle) inEdge.sourceHandle = sourceNodeHandle;

				let outEdge = {
					id: `xy-edge__${draggedNode.id}-${closestEdge.target}`,
					source: draggedNode.id,
					target: closestEdge.target,
					...defaultEdgeOptions,
				};

				const reps = [];
				if (!hasEdge(inEdge.source, inEdge.target, inEdge.sourceHandle)) {
					inEdge.id = generateUniqueEdgeId(inEdge, edgesAtualizados);
					reps.push(withEdgeStyleForHandle(inEdge, defaultEdgeOptions));
				}
				if (!hasEdge(outEdge.source, outEdge.target)) {
					outEdge.id = generateUniqueEdgeId(outEdge, edgesAtualizados);
					reps.push(withEdgeStyleForHandle(outEdge, defaultEdgeOptions));
				}
				return reps;
			}
		};

		const replacements = buildReplacements();
		if (!replacements || replacements.length === 0) return;

		let updatedEdges = edgesAtualizados.flatMap(e => e.id === closestEdge.id ? replacements : [e]);
		// Limpa highlight
		updatedEdges = updatedEdges.map(e => {
			if (e?.style?.strokeDasharray) {
				const ns = { ...e.style };
				delete ns.strokeDasharray;
				return { ...e, style: ns };
			}
			return e;
		});
		myStore.dispatch(setEdges(updatedEdges));
		setHighlightedEdgeIds([]);
		window.dispatchEvent(new CustomEvent('connectPreviewTooltip', { detail: { show: false } }));
		// aplica remoções pendentes (se o usuário deletou durante o arrasto)
		if (pendingRemovalRef.current.size) {
			const ids = Array.from(pendingRemovalRef.current);
			pendingRemovalRef.current.clear();
			ids.forEach(id => performNodeRemoval(id));
		}
	}, [screenToFlowPosition, setHighlightedEdgeIds, highlightedEdgeIds, defaultEdgeOptions]);

	// Início da prévia: limpa highlights
	const onNodeDragStart = useCallback(() => {
		isDraggingRef.current = true;
		const edgesAtual = myStore.getState().edges.edges;
		const cleaned = edgesAtual.map(e => {
			if (e?.style?.strokeDasharray) {
				const ns = { ...e.style };
				delete ns.strokeDasharray;
				return { ...e, style: ns };
			}
			return e;
		});
		myStore.dispatch(setEdges(cleaned));
		setHighlightedEdgeIds([]);
		window.dispatchEvent(new CustomEvent('connectPreviewTooltip', { detail: { show: false } }));
	}, [setHighlightedEdgeIds]);

	// Durante o arrasto do node: destaca a edge mais próxima e envia tooltip
	const onNodeDrag = useCallback((event, draggedNode) => {
		const edgesAtualizados = myStore.getState().edges.edges;
		const allNodes = myStore.getState().nodes.nodes;
		const mousePos = screenToFlowPosition({ x: event.clientX, y: event.clientY });

		const getNodeDims = (n) => ({ w: n?.measured?.width || 150, h: n?.measured?.height || 50 });
		let best = { id: null, dist: Infinity };

		for (const edge of edgesAtualizados) {
			if (edge.source === draggedNode.id || edge.target === draggedNode.id) continue;
			const sourceNode = allNodes.find(n => n.id === edge.source);
			const targetNode = allNodes.find(n => n.id === edge.target);
			if (!sourceNode || !targetNode) continue;
			if (sourceNode.type === 'Repetir' && edge.sourceHandle === 'loop') continue;

			const { w: sw, h: sh } = getNodeDims(sourceNode);
			const { w: tw } = getNodeDims(targetNode);
			let a, b;
			if (sourceNode.type === 'Decidir') {
				if (edge.sourceHandle === 'verdadeiro') {
					a = { x: sourceNode.position.x + sw, y: sourceNode.position.y + sh / 2 };
				} else if (edge.sourceHandle === 'falso') {
					a = { x: sourceNode.position.x, y: sourceNode.position.y + sh / 2 };
				} else {
					continue;
				}
				b = { x: targetNode.position.x + tw / 2, y: targetNode.position.y };
			} else {
				a = { x: sourceNode.position.x + sw / 2, y: sourceNode.position.y + sh };
				b = { x: targetNode.position.x + tw / 2, y: targetNode.position.y };
			}
			const d = distanceFromPointToLineSegment(mousePos, a, b);
			if (d < best.dist) best = { id: edge.id, dist: d };
		}

		const threshold = 20;
		const newHighlighted = best.id && best.dist < threshold ? [best.id] : [];

		// Atualiza edges para pontilhar apenas a mais próxima
		const edgesNew = edgesAtualizados.map(e => {
			const shouldDash = newHighlighted.includes(e.id);
			let style = { ...(e.style || {}) };
			if (shouldDash) {
				style.strokeDasharray = '5,5';
			} else if (style.strokeDasharray) {
				delete style.strokeDasharray;
			}
			return { ...e, style };
		});
		myStore.dispatch(setEdges(edgesNew));
		setHighlightedEdgeIds(newHighlighted);

		// Tooltip de preview
		window.dispatchEvent(new CustomEvent('connectPreviewTooltip', {
			detail: {
				show: newHighlighted.length > 0,
				x: event.clientX + 10,
				y: event.clientY + 10
			}
		}));
	}, [screenToFlowPosition, setHighlightedEdgeIds]);

	const onDelete = useCallback((deletedItens) => {
		// Se está arrastando, adia a remoção para o término do arrasto
		if (isDraggingRef.current && deletedItens?.nodes?.length) {
			deletedItens.nodes.forEach(n => pendingRemovalRef.current.add(n.id));
			return;
		}
		const actuallyEdges = myStore.getState().edges.edges;
		const actuallyNodes = myStore.getState().nodes.nodes;

		if (deletedItens.nodes.length === 0 || deletedItens.edges.length < 2) return;

		const nodeId = deletedItens.nodes[0].id;

		const connectedEdges = deletedItens.edges.filter(
			(e) => e.source === nodeId || e.target === nodeId
		);

		const incomingEdges = connectedEdges.filter((e) => e.target === nodeId);
		const outgoingEdges = connectedEdges.filter((e) => e.source === nodeId);

		console.log('=====================')
		console.log('onDelete')
		console.log('=====================')
		console.log(deletedItens)
		console.log(actuallyEdges)
		console.log(actuallyNodes)
		console.log(nodeId)
		console.log(connectedEdges)
		console.log(incomingEdges)
		console.log(outgoingEdges)
		console.log('=====================')

		let newEdges = [];

		// Conecta os nodes que estavam conectados ao node deletado
		if (incomingEdges.length === 1 && outgoingEdges.length >= 1) {
			const incomingEdge = incomingEdges[0];
			const outgoingEdge = outgoingEdges[0];

			let newEdge = {};

			console.log('=====================')
			console.log('onDelete - edges - parte 1')
			console.log('=====================')
			console.log(incomingEdge)
			console.log(incomingEdge.id.includes('verdadeiro'))
			console.log(incomingEdge.id.includes('falso'))

			// Se for de decisao ele verifica o ponto de origem da aresta para criar a nova aresta a partir do ponto correto
			if (incomingEdge.id.includes('verdadeiro') || incomingEdge.id.includes('falso')) {
				console.log("teste")
				newEdge = {
					id: `xy-edge__${incomingEdge.source}${incomingEdge.id.includes('verdadeiro') ? 'verdadeiro' : 'falso'}-${outgoingEdge.target}`,
					source: incomingEdge.source,
					sourceHandle: incomingEdge.id.includes('verdadeiro') ? 'verdadeiro' : 'falso',
					target: outgoingEdge.target,
					...defaultEdgeOptions,
				};
			} else {
				newEdge = {
					id: `xy-edge__${incomingEdge.source}-${outgoingEdge.target}`,
					source: incomingEdge.source,
					target: outgoingEdge.target,
					...defaultEdgeOptions,
				};
			}

			// aplica ID único e estilo conforme handle
			newEdge.id = generateUniqueEdgeId(newEdge, actuallyEdges);
			newEdge = withEdgeStyleForHandle(newEdge, defaultEdgeOptions);

			console.log('=====================')
			console.log('onDelete - edges - parte 2')
			console.log('=====================')
			console.log(incomingEdge)
			console.log(outgoingEdge)
			console.log(newEdge)
			console.log('=====================')


			newEdges = actuallyEdges
				.filter(
					(e) =>
						e.id !== incomingEdge.id &&
						e.id !== outgoingEdge.id &&
						e.source !== nodeId &&
						e.target !== nodeId
				)
				.concat(newEdge);
		} else {
			// Remove as arestas conectadas ao nó deletado
			newEdges = actuallyEdges.filter((e) => e.source !== nodeId && e.target !== nodeId);
		}

		myStore.dispatch(setEdges(newEdges));

	}, []);

	// Quando conectar dois nodes
	const onConnect = useCallback(
		(params) => {
			const edgesAtualizados = myStore.getState().edges.edges;

			console.log('=====================')
			console.log('onConnect')
			console.log('=====================')
			console.log(params)
			console.log(edgesAtualizados)
			console.log('=====================')

			// Permite múltiplas conexões em targets (sem bloqueio por já existir conexão)
			// Mas limita as saídas 'verdadeiro' e 'falso' a apenas 1 conexão por handle
			if (params.sourceHandle === 'verdadeiro' || params.sourceHandle === 'falso') {
				const alreadyFromThisHandle = edgesAtualizados.filter(e => 
					e.source === params.source && e.sourceHandle === params.sourceHandle
				);
				if (alreadyFromThisHandle.length >= 1) {
					console.log(`Conexão bloqueada: handle '${params.sourceHandle}' já possui uma saída`);
					return;
				}
			}

			// Gera ID único para a edge incluindo os handles (se existirem)
			const sHandlePart = params.sourceHandle ? `${params.sourceHandle}` : '';
			const tHandlePart = params.targetHandle ? `${params.targetHandle}` : '';
			const baseId = `xy-edge__${params.source}${sHandlePart}-${params.target}${tHandlePart}`;
			let uniqueId = baseId;
			if (edgesAtualizados.some(e => e.id === uniqueId)) {
				// Se já existir, adiciona um sufixo incremental para manter unicidade
				const count = edgesAtualizados.filter(e => e.id.startsWith(baseId)).length;
				uniqueId = `${baseId}__${count + 1}`;
			}
			params.id = uniqueId;

			// aplica estilo baseado no handle antes de salvar
			const styledEdge = withEdgeStyleForHandle(params, defaultEdgeOptions);
			const newEdges = edgesAtualizados.concat(styledEdge);
			myStore.dispatch(setEdges(newEdges));
		},
		[setEdges, defaultEdgeOptions]
	);

	// Função para calcular a distância de um ponto a um segmento de linha
	const distanceFromPointToLineSegment = (p, a, b) => {
		const x = p.x;
		const y = p.y;
		const x1 = a.x;
		const y1 = a.y;
		const x2 = b.x;
		const y2 = b.y;

		const A = x - x1;
		const B = y - y1;
		const C = x2 - x1;
		const D = y2 - y1;

		const dot = A * C + B * D;
		const len_sq = C * C + D * D;
		let param = -1;
		if (len_sq !== 0) param = dot / len_sq;

		let xx, yy;

		if (param < 0) {
			xx = x1;
			yy = y1;
		} else if (param > 1) {
			xx = x2;
			yy = y2;
		} else {
			xx = x1 + param * C;
			yy = y1 + param * D;
		}

		const dx = x - xx;
		const dy = y - yy;
		return Math.sqrt(dx * dx + dy * dy);
	}

	// Quando arrastar algo sobre o fluxo
	const onDragOver = useCallback(
		(event) => {
			const edges = myStore.getState().edges.edges;
			const nodes = myStore.getState().nodes.nodes;

			event.preventDefault();
			event.dataTransfer.dropEffect = 'move';

			const position = screenToFlowPosition({
				x: event.clientX,
				y: event.clientY,
			});

			const nearbyEdgeIds = edges
				.filter((edge) => {
					const sourceNode = nodes.find((node) => node.id === edge.source);
					const targetNode = nodes.find((node) => node.id === edge.target);

					if (!sourceNode || !targetNode) return false;

					let posicaoInicioLinhaFalso = {};
					let posicaoInicioLinhaVerdadeiro = {};
					let posicaoFimLinha = {};

					if (sourceNode.type === "Decidir") {
						posicaoInicioLinhaFalso = {
							x: sourceNode.position.x,
							y: sourceNode.position.y + (sourceNode.measured.height / 2),
						};
						posicaoInicioLinhaVerdadeiro = {
							x: sourceNode.position.x + sourceNode.measured.width,
							y: sourceNode.position.y + (sourceNode.measured.height / 2),
						};
						posicaoFimLinha = {
							x: targetNode.position.x + (targetNode.measured.width / 2),
							y: targetNode.position.y,
						};

						const distanceFalso = distanceFromPointToLineSegment(
							position,
							posicaoInicioLinhaFalso,
							posicaoFimLinha
						);

						const distanceVerdadeiro = distanceFromPointToLineSegment(
							position,
							posicaoInicioLinhaVerdadeiro,
							posicaoFimLinha
						);

						if (distanceFalso < 20 && edge.sourceHandle === 'falso') {
							return true;
						}

						if (distanceVerdadeiro < 20 && edge.sourceHandle === 'verdadeiro') {
							return true;
						}

						return false;
					} else {
						posicaoInicioLinhaFalso = {
							x: sourceNode.position.x + (sourceNode.measured.width / 2),
							y: sourceNode.position.y + sourceNode.measured.height,
						};
						posicaoInicioLinhaVerdadeiro = posicaoInicioLinhaFalso;
						posicaoFimLinha = {
							x: targetNode.position.x + (targetNode.measured.width / 2),
							y: targetNode.position.y,
						};

						const distance = distanceFromPointToLineSegment(
							position,
							posicaoInicioLinhaFalso,
							posicaoFimLinha
						);

						return distance < 20;
					}
				})
				.map((edge) => edge.id);
			const edgesNew = edges.map((edge) => {
				if (nearbyEdgeIds.includes(edge.id)) {
					return {
						...edge,
						style: {
							...edge.style,
							strokeDasharray: '5,5',
						},
					};
				} else if (highlightedEdgeIds.includes(edge.id)) {
					// Resetar estilo se estava previamente destacado
					const newStyle = { ...edge.style };
					delete newStyle.strokeDasharray;
					return {
						...edge,
						style: newStyle,
					};
				} else {
					return edge;
				}
			})

			myStore.dispatch(setEdges(edgesNew));

			setHighlightedEdgeIds(nearbyEdgeIds);
		},
		[
			edges,
			nodes,
			screenToFlowPosition,
			highlightedEdgeIds,
			setHighlightedEdgeIds,
		]
	);

	// Quando soltar um novo node no fluxo
	const onDrop = useCallback(
		(event) => {
			const edgesAtualizados = myStore.getState().edges.edges;
			event.preventDefault();

			const type = event.dataTransfer.getData('application/reactflow');
			if (!type) return;

			// Lista de tipos que só podem ser usados como sub-nodes
			const operatorTypes = [
				'OperatorSum', 'OperatorSubtract', 'OperatorEqual', 'OperatorNotEqual',
				'OperatorGreater', 'OperatorLess', 'OperatorGreaterEqual', 'OperatorLessEqual',
				'Soma', 'Subtração', 'Multiplicação', 'Divisão', 'Potenciação', 'Radiciação',
				'Igual a', 'Diferente', 'Diferente de', 'Maior que', 'Menor que',
				'Maior ou igual', 'Maior ou igual a', 'Menor ou igual', 'Menor ou igual a',
				'Conjunção/Interseção', 'Disjunção inclusiva/União', 'Negação/complementação', 'Disjunção exclusiva'
			];

			// Verifica se é um operador tentando ser solto diretamente
			if (operatorTypes.includes(type)) {
				// Dispara evento customizado para mostrar modal de operador
				const event = new CustomEvent('showOperatorModal', {
					detail: { operatorType: type }
				});
				window.dispatchEvent(event);
				return; // Não cria o nó
			}

			const isInputVarDrop = document.elementFromPoint(event.clientX, event.clientY)?.getAttribute('data-lernode-varinput') === 'true';
			const isVarDrop = !!event.dataTransfer.types.find(t => t === 'application/variavel');
			if (isInputVarDrop && !isVarDrop) {
				// Drop inválido: não cria node
				return;
			}

			const initialPosition = screenToFlowPosition({
				x: event.clientX,
				y: event.clientY,
			});

			// Dados padrão específicos para cada tipo de nó
			let defaultData = { label: `${type}` };
			
			if (type === 'Fundo') {
				defaultData = {
					...defaultData,
					backgroundUrl: '',
					backgroundFit: 'cover'
				};
			}
			
			// Para RepetirNode, adiciona valores padrão para evitar validação
			if (type === 'Repetir') {
				defaultData = {
					...defaultData,
					tipoRepeticao: 'quantidade',
					quantidade: '3',
					condicao: '',
					dropDataCondicao: { 
						id: 'condicao', 
						type: null, 
						data: {} 
					},
					variavel: '',
					incremento: '1',
					valorInicial: '1',
					valorFinal: '3'
				};
			}

			const newNode = {
				id: getId(),
				type,
				position: initialPosition,
				data: defaultData,
			};

			// 1) Descobre qual é a edge mais próxima do ponto do drop (para evitar alterar várias)
			const allNodes = myStore.getState().nodes.nodes;
			const getNodeDims = (n) => ({
				w: n?.measured?.width || 150,
				h: n?.measured?.height || 50,
			});

			const findClosestEdge = () => {
				let best = { edge: null, distance: Infinity };
				for (const edge of edgesAtualizados) {
					const sourceNode = allNodes.find((n) => n.id === edge.source);
					const targetNode = allNodes.find((n) => n.id === edge.target);
					if (!sourceNode || !targetNode) continue;
					// Ignora loop do Repetir
					if (sourceNode.type === 'Repetir' && edge.sourceHandle === 'loop') continue;

					const { w: sw, h: sh } = getNodeDims(sourceNode);
					const { w: tw } = getNodeDims(targetNode);

					let a, b;
					if (sourceNode.type === 'Decidir') {
						// Cada edge de decisão sai de um dos lados:
						if (edge.sourceHandle === 'verdadeiro') {
							a = { x: sourceNode.position.x + sw, y: sourceNode.position.y + sh / 2 };
						} else if (edge.sourceHandle === 'falso') {
							a = { x: sourceNode.position.x, y: sourceNode.position.y + sh / 2 };
						} else {
							// sem handle conhecido, pula
							continue;
						}
						b = { x: targetNode.position.x + tw / 2, y: targetNode.position.y };
					} else {
						a = { x: sourceNode.position.x + sw / 2, y: sourceNode.position.y + sh };
						b = { x: targetNode.position.x + tw / 2, y: targetNode.position.y };
					}

					const d = distanceFromPointToLineSegment(initialPosition, a, b);
					if (d < best.distance) {
						best = { edge, distance: d };
					}
				}
				return best;
			};

			const { edge: closestEdge, distance: closestDistance } = findClosestEdge();
			console.log('[onDrop] closestEdge:', closestEdge?.id, 'distance:', closestDistance);

			// 2) Constrói nova lista de edges substituindo apenas a edge escolhida (se suficientemente perto)
			let updatedEdges = edgesAtualizados;
			const threshold = 20; // px
			if (closestEdge && closestDistance < threshold) {
				const sourceNodeHandle = closestEdge.sourceHandle; // pode ser 'verdadeiro' | 'falso' | undefined

				const replacements = (() => {
					if (type === 'Decidir') {
						// Inserindo um nó de decisão: conecta origem -> novo (preserva handle se havia)
						let newEdge1 = {
							id: `xy-edge__${closestEdge.source}${sourceNodeHandle || ''}-${newNode.id}`,
							source: closestEdge.source,
							target: newNode.id,
							...defaultEdgeOptions,
						};
						if (sourceNodeHandle) newEdge1.sourceHandle = sourceNodeHandle;

						let newEdge2 = {
							id: `xy-edge__${newNode.id}verdadeiro-${closestEdge.target}`,
							source: newNode.id,
							sourceHandle: 'verdadeiro',
							target: closestEdge.target,
							...defaultEdgeOptions,
						};
						let newEdge3 = {
							id: `xy-edge__${newNode.id}falso-${closestEdge.target}`,
							source: newNode.id,
							sourceHandle: 'falso',
							target: closestEdge.target,
							...defaultEdgeOptions,
						};

						newEdge1.id = generateUniqueEdgeId(newEdge1, edgesAtualizados);
						newEdge2.id = generateUniqueEdgeId(newEdge2, edgesAtualizados);
						newEdge3.id = generateUniqueEdgeId(newEdge3, edgesAtualizados);
						newEdge1 = withEdgeStyleForHandle(newEdge1, defaultEdgeOptions);
						newEdge2 = withEdgeStyleForHandle(newEdge2, defaultEdgeOptions);
						newEdge3 = withEdgeStyleForHandle(newEdge3, defaultEdgeOptions);
						return [newEdge1, newEdge2, newEdge3];
					} else {
						// Nó comum: divide a edge em duas (preservando handle na primeira)
						let newEdge1 = {
							id: `xy-edge__${closestEdge.source}${sourceNodeHandle || ''}-${newNode.id}`,
							source: closestEdge.source,
							target: newNode.id,
							...defaultEdgeOptions,
						};
						if (sourceNodeHandle) newEdge1.sourceHandle = sourceNodeHandle;

						let newEdge2 = {
							id: `xy-edge__${newNode.id}-${closestEdge.target}`,
							source: newNode.id,
							target: closestEdge.target,
							...defaultEdgeOptions,
						};

						newEdge1.id = generateUniqueEdgeId(newEdge1, edgesAtualizados);
						newEdge2.id = generateUniqueEdgeId(newEdge2, edgesAtualizados);
						newEdge1 = withEdgeStyleForHandle(newEdge1, defaultEdgeOptions);
						newEdge2 = withEdgeStyleForHandle(newEdge2, defaultEdgeOptions);
						return [newEdge1, newEdge2];
					}
				})();

				updatedEdges = edgesAtualizados.flatMap(e => e.id === closestEdge.id ? replacements : [e]);
			}

			// Limpa qualquer highlight (strokeDasharray) remanescente
			const cleanedEdges = updatedEdges.map(e => {
				if (e?.style?.strokeDasharray) {
					const ns = { ...e.style };
					delete ns.strokeDasharray;
					return { ...e, style: ns };
				}
				return e;
			});
			myStore.dispatch(setEdges(cleanedEdges));

			const addNode = nodes.concat(newNode);
			myStore.dispatch(setNodes(addNode));

			setHighlightedEdgeIds([]);
		},
		[
			screenToFlowPosition,
			nodes,
			defaultEdgeOptions,
			highlightedEdgeIds,
			setHighlightedEdgeIds,
		]
	);

	// Quando sair da área de arrasto
	const onDragLeave = useCallback(() => {
		// Resetar estilos das arestas destacadas
		const edgesNew = edges.map((edge) => {
			if (highlightedEdgeIds.includes(edge.id)) {
				const newStyle = { ...edge.style };
				delete newStyle.strokeDasharray;
				return {
					...edge,
					style: newStyle,
				};
			} else {
				return edge;
			}
		})
		myStore.dispatch(setEdges(edgesNew));
		setHighlightedEdgeIds([]);
		// Esconde tooltip caso esteja visível
		window.dispatchEvent(new CustomEvent('connectPreviewTooltip', { detail: { show: false } }));
	}, [highlightedEdgeIds, setHighlightedEdgeIds]);


	const onEdgesChange = useCallback((changes) => {
		const actuallyEdges = myStore.getState().edges.edges;
		console.log('=====================')
		console.log('onEdgesChange')
		console.log('=====================')
		console.log(changes)
		console.log(actuallyEdges)
		console.log('=====================')

		const edgesNew = applyEdgeChanges(changes, actuallyEdges)

		console.log('=====================')
		console.log('onEdgesChange')
		console.log('=====================')
		console.log(changes)
		console.log(actuallyEdges)
		console.log(edgesNew)
		console.log('=====================')

		myStore.dispatch(setEdges(edgesNew));
	});

	// Quando os nodes mudam de posição ou são deletados (ou adicionados) no fluxo, isso faz com que as arestas sejam atualizadas
	// e as arestas conectadas a um node deletado sejam removidas e substituídas por uma nova aresta conectando os nodes conectados
	const onNodesChange = useCallback((changes) => {
		const actuallyEdges = myStore.getState().edges.edges;
		// Clona lista de nodes
		let nodesComPosicao = myStore.getState().nodes.nodes;
		// Se houve remoção de nó (ex.: deletado durante arrasto), limpamos highlights e tooltip
		const hasRemove = changes.some(c => c.type === 'remove');
		if (hasRemove) {
			const edgesClean = actuallyEdges.map(e => {
				if (e?.style?.strokeDasharray) {
					const ns = { ...e.style };
					delete ns.strokeDasharray;
					return { ...e, style: ns };
				}
				return e;
			});
			myStore.dispatch(setEdges(edgesClean));
			setHighlightedEdgeIds([]);
			window.dispatchEvent(new CustomEvent('connectPreviewTooltip', { detail: { show: false } }));
		}
		// Se está arrastando, não deixa o ReactFlow remover agora; adia a remoção
		let filteredChanges = changes;
		if (isDraggingRef.current && hasRemove) {
			const idsToRemove = changes.filter(c => c.type === 'remove').map(c => c.id);
			idsToRemove.forEach(id => pendingRemovalRef.current.add(id));
			filteredChanges = changes.filter(c => c.type !== 'remove');
		}
		// Processa mudanças de dados customizadas (type: 'update')
		const dataUpdates = filteredChanges.filter(c => c.type === 'update');
		if (dataUpdates.length > 0) {
			nodesComPosicao = nodesComPosicao.map(node => {
				const upd = dataUpdates.find(u => u.id === node.id);
				return upd ? { ...node, data: { ...node.data, ...upd.data } } : node;
			});
			// atualiza nodes no store para disparar histórico
			myStore.dispatch(setNodes(nodesComPosicao));
		}
		// Considera apenas outras mudanças para posição/remocao/dimensões
		const nodeChanges = filteredChanges.filter(c => c.type !== 'update');

		console.log('=====================')
		console.log('onNodesChange')
		console.log('=====================')
		console.log(changes)
		console.log(actuallyEdges)
		console.log('=====================')

		nodeChanges.forEach((change) => {
			if (change.type === 'remove') {
				const nodeId = change.id;
				let newEdges = [];
				const connectedEdges = actuallyEdges.filter(
					(e) => e.source === nodeId || e.target === nodeId
				);

				const incomingEdges = connectedEdges.filter((e) => e.target === nodeId);
				const outgoingEdges = connectedEdges.filter((e) => e.source === nodeId);

				console.log('=====================')
				console.log('onNodesChange - edges - parte 1')
				console.log('=====================')
				console.log(incomingEdges)
				console.log(connectedEdges)
				console.log(outgoingEdges)
				console.log(actuallyEdges)
				console.log(nodeId)
				console.log('=====================')

				if (incomingEdges.length === 1 && outgoingEdges.length >= 1) {
					const incomingEdge = incomingEdges[0];
					const outgoingEdge = outgoingEdges[0];

					let newEdge = {
						id: `xy-edge__${incomingEdge.source}-${outgoingEdge.target}`,
						source: incomingEdge.source,
						target: outgoingEdge.target,
						...defaultEdgeOptions,
					};

					// se a edge original vinha de 'verdadeiro' ou 'falso', preserva handle e cor
					if (incomingEdge.sourceHandle === 'verdadeiro' || incomingEdge.sourceHandle === 'falso') {
						newEdge.sourceHandle = incomingEdge.sourceHandle;
					}
					newEdge.id = generateUniqueEdgeId(newEdge, actuallyEdges);
					newEdge = withEdgeStyleForHandle(newEdge, defaultEdgeOptions);

					console.log('=====================')
					console.log('onNodesChange - edges - parte 2')
					console.log('=====================')
					console.log(incomingEdge)
					console.log(outgoingEdge)
					console.log(newEdge)
					console.log('=====================')


					newEdges = actuallyEdges
						.filter(
							(e) =>
								e.id !== incomingEdge.id &&
								e.id !== outgoingEdge.id &&
								e.source !== nodeId &&
								e.target !== nodeId
						)
						.concat(newEdge);
				} else {
					// Remove as arestas conectadas ao nó deletado
					newEdges = actuallyEdges.filter((e) => e.source !== nodeId && e.target !== nodeId);
				}

				// Atualize as arestas
				myStore.dispatch(setEdges(newEdges));
			} else if (change.type === 'dimensions') {
				const node = nodes.find((n) => n.id === change.id);
				// Se não achar o node ele retorna
				if (!node) return;
				// Se o node ja tem altura e largura ele retorna
				if (node.measured?.width && node.measured?.height) return;

				const nodeWidth = change.dimensions.width;
				const nodeHeight = change.dimensions.height;

				const adjustedPosition = {
					x: node.position.x - nodeWidth / 2,
					y: node.position.y - nodeHeight / 2,
				};

				// Se o node não tem altura e largura ele atualiza seu posicionamento pois é um node novo
				nodesComPosicao = nodesComPosicao.map((nodePos) =>
					nodePos.id === node.id ? { ...nodePos, position: adjustedPosition } : nodePos
				);
			}
		});

		// Aplica mudanças de posição, dimensionamento e remoção/adicionamento, se houver
		if (nodeChanges.length > 0) {
			const nodesNew = applyNodeChanges(nodeChanges, nodesComPosicao);
			myStore.dispatch(setNodes(nodesNew));
		}

	}, [defaultEdgeOptions, edges, setHighlightedEdgeIds]);

	return { 
		onConnect, 
		onDragOver, 
		onDrop, 
		onDragLeave, 
		onNodesChange, 
		onEdgesChange, 
		onNodeDragStop,
		onNodeDragStart,
		onNodeDrag,
		onDelete,
		organizeNodesLayout,
		organizeNodesVertical,
		organizeNodesHorizontal,
		organizeNodesWithBranches,
		autoOrganizeIfNeeded,
		getOptimalSpacing
	};
};

/*
EXEMPLO DE USO DAS FUNÇÕES DE ORGANIZAÇÃO:

// Para organizar nós verticalmente centralizados (recomendado):
organizeNodesVertical();

// Para organizar nós horizontalmente:
organizeNodesHorizontal();

// Para organização customizada:
organizeNodesLayout({
    spacing: 200,        // Espaçamento entre nós
    arrowSpacing: 30,    // Espaço extra para setas
    startX: 300,         // Posição X inicial
    startY: 50,          // Posição Y inicial
    centerHorizontally: true, // Centralizar horizontalmente
    mode: 'vertical'     // 'vertical', 'horizontal'
});

// Para organização avançada com ramificações:
organizeNodesWithBranches();

// Para verificar e organizar automaticamente se necessário:
autoOrganizeIfNeeded();

// Para obter espaçamento otimizado entre dois nós:
const spacing = getOptimalSpacing(node1, node2);

// Para usar com fitView automático, passe a instância do ReactFlow:
const { fitView } = useReactFlow();
const reactFlowInstance = { fitView };

organizeNodesVertical(reactFlowInstance);
organizeNodesHorizontal(reactFlowInstance);
organizeNodesWithBranches(reactFlowInstance);
autoOrganizeIfNeeded(reactFlowInstance);

As funções usam as propriedades node.measured.width e node.measured.height 
quando disponíveis, que são as dimensões reais dos nós renderizados.

NOVO: Todas as funções de organização agora suportam fitView automático
quando a instância do ReactFlow é fornecida como parâmetro.
*/
